import { AUTHZ_AUTHN_ERROR, HOME_PATH } from 'services/AuthSuite/constants';
import TrackingService, { ACTION_NAMES, APP_LOG_TYPE } from 'services/TrackingService';
import Domain from 'helpers/Domain';

export default {
    namespaced: true,

    state: {
        accessStatus: null,
        apiService: {},
        authSuiteRegion: null,
        authSuiteSdkProvider: null,
        authSuiteServices: {},
        callbackUrl: null,
        isEnabled: null,
        isLoaded: false,
        linkedDeviceWithProvider: false,
        mvpdLoginEventBound: false,
        selectedProviderCode: '',
        selectedProviderName: 'unknown',
        tokens: null,
        tokenStatus: null,
    },

    getters: {
        getAuthSuiteServices: (state) => {
            return state.authSuiteServices;
        },
        isEnabled: (state) => {
            return state.isEnabled;
        },
        mvpdLoginEventBound: (state) => {
            return state.mvpdLoginEventBound;
        },
        selectedProviderCode: (state) => {
            return state.selectedProviderCode || sessionStorage.getItem('selectedProviderCode');
        },
    },

    mutations: {
        authSuiteEnabledStatus(state, payload) {
            state.isEnabled = payload;
        },
        getAuthSuiteServices(state) {
            state.authSuiteServices = state.authSuiteSdkProvider.getAuthSuiteServices();
        },
        setTokens(state, payload) {
            state.tokens = payload;
        },
        setUpAuthSuiteSdkProvider(
            state,
            {
                enabled,
                clientId,
                countryCode,
                regionBaseUrl,
                callbackUrl,
                env,
                AuthSuiteSdkProvider,
            },
        ) {
            if (enabled) {
                const region = CBS.Registry.region.property;
                const authSuiteCountryCode = countryCode || region;
                state.authSuiteSdkProvider = new AuthSuiteSdkProvider({
                    clientId,
                    countryCode: authSuiteCountryCode,
                    env,
                });
                state.authSuiteRegion = region || countryCode;
                state.callbackUrl = callbackUrl;
            }
        },
        tokenStatus(state, payload) {
            state.tokenStatus = payload;
        },
        updateAccessStatus(state, payload) {
            state.accessStatus = payload;
            state.isLoaded = true;
        },
        updateLinkedDeviceWithProvider: (state, payload) => {
            state.linkedDeviceWithProvider = payload;
        },
        updateMvpdLoginEventBound: (state, payload) => {
            state.mvpdLoginEventBound = payload;
        },
        updateSelectedProviderCode: (state, payload) => {
            sessionStorage.setItem('selectedProviderCode', payload);
            state.selectedProviderCode = payload;
        },
        updateSelectedProviderName: (state, payload) => {
            state.selectedProviderName = payload;
        },
    },
    actions: {
        /**
         * @param {*} param0
         * @param {object} payload
         * @param {string} payload.authorizationCode
         * @param {string} payload.authorizedResourceGroup
         * @param {function} payload.errorCallback
         * @param {function} payload.successCallback
         */
        async authorizationCallback({ dispatch, getters, state }, payload = {}) {
            try {
                await dispatch('linkDeviceWithProvider', payload.authorizationCode);
                await dispatch('checkAccessStatus');

                const authorized = await dispatch('hasAuthorizedResourceGroup', {
                    authorizedResourceGroup: payload.authorizedResourceGroup,
                });

                if (authorized) {
                    if (typeof payload.successCallback === 'function') {
                        payload.successCallback();
                    }
                } else if (typeof payload.errorCallback === 'function') {
                    const providerObj = {
                        code: getters['selectedProviderCode'],
                    };

                    dispatch('trackAuthFail', {
                        providerObj,
                        appLogType: APP_LOG_TYPE.NO_AUTH_Z,
                    });

                    payload.errorCallback();
                }
            } catch (e) {}
        },

        /**
         * @param {*} param0
         * @param {object} payload
         * @param {string} payload.authorizedResourceGroup
         * @param {function} payload.errorCallback
         * @param {function} payload.successCallback
         *
         * @returns
         */
        async bindLoginEventAuthorizationCallbacks({ dispatch, state }, payload = {}) {
            if (state.isLoaded) {
                return dispatch('bindLoginEventHandler', async (authorizationCode) => {
                    try {
                        payload.authorizationCode = authorizationCode;
                        await dispatch('authorizationCallback', payload);
                    } catch (e) {}
                });
            }
        },

        bindLoginEventHandler({ commit, dispatch, state }, callback) {
            if (!state.mvpdLoginEventBound) {
                state.authSuiteServices.messagingService.registerPostLoginMessaging();
                state.authSuiteServices.loginEvent.onSuccess((authorizationCode) => {
                    // Get Access Token and Refresh Token
                    callback(authorizationCode);
                });
                commit('updateMvpdLoginEventBound', true);
            }
        },
        // Access Status must happen on every AuthSuite Call
        async checkAccessStatus({ commit, dispatch, state }) {
            const { accessService } = state.authSuiteServices;
            let accessStatus;

            try {
                accessStatus = await accessService.checkAccessStatus();
                commit('updateAccessStatus', accessStatus);
                commit('tokenStatus', 'requesting');
                await dispatch('getTokens');
            } catch (e) {
                commit('tokenStatus', e.errorCode);
                commit('updateAccessStatus', e);
            }
        },
        emitAlreadyBoundError({ commit, dispatch, state }, providerObj = {}) {
            const { loginEvent } = state.authSuiteServices;
            // TODO: fix issue with these redirects for domestic, shouldn't need below ternary
            let regionPath = Domain.isInternational()
                ? `/${state.authSuiteRegion.toLowerCase()}`
                : HOME_PATH;
            loginEvent.emitFail({
                errorType: AUTHZ_AUTHN_ERROR,
                overrides: {
                    brandName: 'Paramount+',
                    partner: state.accessStatus.accessMethodsInUse[0]?.cobranding.name,
                },
                heading: 'active_user_package_exists_title',
                subHeading: 'active_user_package_exists_description',
                primaryButton: {
                    label: 'close',
                    link: regionPath,
                },
            });
        },
        emitAlreadySubscribedError({ commit, dispatch, state }, providerObj = {}) {
            const { loginEvent } = state.authSuiteServices;
            let regionPath = Domain.isInternational()
                ? `/${state.authSuiteRegion.toLowerCase()}`
                : HOME_PATH;
            loginEvent.emitFail({
                errorType: AUTHZ_AUTHN_ERROR,
                overrides: {
                    brandName: 'Paramount+',
                    provider: state.accessStatus.accessMethodsInUse[0]?.cobranding.name,
                },
                heading: 'subscription_exists_title',
                subHeading: 'subscription_exists_description',
                primaryButton: {
                    label: 'close',
                    link: regionPath,
                },
            });
        },
        emitAlreadyBoundPromoCodeError({ commit, dispatch, state }, providerObj = {}) {
            const { loginEvent } = state.authSuiteServices;
            loginEvent.emitFail({
                errorType: AUTHZ_AUTHN_ERROR,
                overrides: {
                    brandName: 'Paramount+',
                    partner: state.accessStatus.accessMethodsInUse[0].cobranding.name,
                },
                heading: 'mvpd_connection_already_exists_headline',
                subHeading: 'mvpd_connection_already_exists_subheadline',
                primaryButton: {
                    label: 'close',
                    link: `/${state.authSuiteRegion.toLowerCase()}`,
                },
            });
        },
        emitAuthNError({ commit, dispatch, state }, providerObj = {}) {
            const { loginEvent } = state.authSuiteServices;
            dispatch('trackAuthFail', {
                providerObj,
                appLogType: APP_LOG_TYPE.NO_AUTH_N,
            });

            loginEvent.emitFail({
                errorType: AUTHZ_AUTHN_ERROR,
                overrides: {},
                heading:
                    'there_was_an_issue_authenticating_or_authorizing_your_account_with_your_partner',
                subHeading:
                    'we_will_sign_you_out_please_try_to_sign_in_again_if_you_continue_to_encounter_this_issue_please_contact_your_partner_directly',
                primaryButton: {
                    label: 'close',
                    link: `/${state.authSuiteRegion}`,
                },
            });
        },
        emitAuthZError({ commit, dispatch, state }, providerObj = {}) {
            const { loginEvent } = state.authSuiteServices;
            dispatch('trackAuthFail', {
                providerObj,
                appLogType: APP_LOG_TYPE.NO_AUTH_Z,
            });

            loginEvent.emitFail({
                errorType: AUTHZ_AUTHN_ERROR,
                overrides: {
                    brandName: 'Paramount+',
                },
                heading: 'sorry_you_dont_have_access_to_brandName_within_your_subscription_package',
                subHeading: 'contact_your_provider_and_add_brandName_to_your_package_today',
                primaryButton: {
                    label: 'close',
                    link: `/${state.authSuiteRegion}`,
                },
            });
        },

        /**
         * @param {*} param0
         * @param {string} providerCode
         * @returns
         */
        getProviderAccessMethod({ state }, providerCode) {
            if (state.tokenStatus === 'valid' && state.linkedDeviceWithProvider === true) {
                try {
                    const { accessMethodsInUse } = state.accessStatus;
                    const currentAccessMethod = accessMethodsInUse.find(
                        (accessMethod) => accessMethod.providerCode === providerCode,
                    );
                    return currentAccessMethod;
                } catch (e) {}
            }

            return null;
        },

        /**
         * @param {*} param0
         * @param {string} providerCode
         * @returns
         */
        async getProviderLoginURL({ state }, providerCode) {
            if (!providerCode) {
                return null;
            }

            return state.authSuiteServices.authorizationService
                .getAuthenticationUrl({
                    callbackUrl: state.callbackUrl,
                    mvpdCode: providerCode,
                })
                .then((resp) => {
                    return resp.authenticationUrl;
                })
                .catch((e) => {
                    return null;
                });
        },

        async getTokens({ commit, dispatch, state }, payload = {}) {
            const { accessTokenService, loginEvent } = state.authSuiteServices;
            let tokens;

            try {
                tokens = await accessTokenService.getAccessToken();
                commit('tokenStatus', 'valid');
                commit('setTokens', tokens);
            } catch (e) {
                commit('tokenStatus', 'invalid');
                commit('setTokens', e);
                loginEvent.emitFail(e);
            }
            return tokens;
        },

        /**
         * @param {*} param0
         * @param {*} payload
         * @param {string} payload.authorizedResourceGroup
         * @param {string} payload.providerCode
         * @returns
         */
        async hasAuthorizedResourceGroup({ state, getters, dispatch }, payload = {}) {
            try {
                const providerCode = payload.providerCode || getters['selectedProviderCode'];
                const accessMethod = await dispatch('getProviderAccessMethod', providerCode);

                return accessMethod?.authorizedResourceGroups?.includes(
                    payload.authorizedResourceGroup,
                );
            } catch (e) {}

            return false;
        },

        /**
         * @param {*} param0
         * @param {string} authorizationCode
         * @returns
         */
        linkDeviceWithProvider({ commit, getters, dispatch, state }, authorizationCode) {
            const selectedProviderCode = getters['selectedProviderCode'];

            if (!selectedProviderCode && newrelic) {
                const err = new Error('No MVPD code');
                newrelic.noticeError(err, {
                    isLoggedIn: getters['user/isLoggedIn'],
                    isExSubscriber: getters['user/isExSubscriber'],
                    isSubscriber: getters['user/isSubscriber'],
                    selectedProviderName: state.selectedProviderName,
                });
            }

            if (!authorizationCode) {
                dispatch('emitAuthNError', {
                    code: selectedProviderCode,
                    name: state.selectedProviderName,
                });
                return;
            }
            if (!state.linkedDeviceWithProvider) {
                return state.authSuiteServices.authorizationService
                    .linkDeviceWithProvider({
                        mvpdCode: selectedProviderCode,
                        authorizationCode,
                    })
                    .then((resp) => {
                        commit('updateLinkedDeviceWithProvider', true);
                        sessionStorage.removeItem('selectedProviderCode');
                    })
                    .catch((err) => {
                        dispatch('emitAuthNError', {
                            code: selectedProviderCode,
                            name: state.selectedProviderName,
                        });
                        sessionStorage.removeItem('selectedProviderCode');
                    });
            }
        },
        loadAuthSuite({ commit, dispatch, state }, payload = {}) {
            commit('authSuiteEnabledStatus', payload.enabled);
            if (payload.enabled) {
                import(
                    /* webpackChunkName: "auth-suite-sdk" */
                    'services/AuthSuite/AuthSuiteSdkProvider.js'
                ).then((module) => {
                    const AuthSuiteSdkProvider = module.default;
                    payload.AuthSuiteSdkProvider = AuthSuiteSdkProvider;
                    commit('setUpAuthSuiteSdkProvider', payload);
                    commit('getAuthSuiteServices');
                    dispatch('checkAccessStatus');
                });
                import(
                    /* webpackChunkName: "api-service" */
                    'services/ApiService'
                ).then((module) => {
                    const { bindWithAuthSuite, refreshUserAccess } = module;

                    state.apiService = {
                        bindWithAuthSuite,
                        refreshUserAccess,
                    };
                });
            }
        },
        async registerWithApi({ state, rootGetters }, payload = {}) {
            let callBackFn = null;
            let trackBindSuccessFn = null;
            const redirectPath = rootGetters['flow/isRendezvous']
                ? rootGetters['flow/destinationUrl']
                : '/home/';

            let successCallbackFn = () => {
                window.location.href = redirectPath;
            };

            if (typeof payload === 'function') {
                callBackFn = payload;
            } else if (typeof payload === 'object') {
                callBackFn = payload?.errorCallBack;
                trackBindSuccessFn = payload?.trackBindSuccess;

                if (typeof payload?.successCallback === 'function') {
                    successCallbackFn = payload?.successCallback;
                }
            }

            const httpData = {
                _clientRegion: state.authSuiteRegion,
                authSuiteToken: state.tokens.applicationAccessToken,
            };
            const result = await state.apiService?.bindWithAuthSuite?.(httpData, callBackFn);

            if (result?.success) {
                if (typeof trackBindSuccessFn === 'function') {
                    trackBindSuccessFn(result?.user);
                }
                successCallbackFn();
            }
        },
        refreshAuthSuiteAccess({ commit, dispatch, state }, callBackFn = null, errorCallBackFn) {
            const httpData = {
                authSuiteToken: state.tokens.applicationAccessToken,
            };
            state.apiService?.refreshUserAccess?.(httpData, callBackFn, errorCallBackFn);
        },

        /**
         * @param {*} param0
         * @param {object} payload
         * @param {string} payload.providerCode
         */
        async selectProvider({ commit }, payload = {}) {
            commit('updateSelectedProviderCode', payload.providerCode);
        },

        trackAuthFail({ commit, dispatch, state }, { providerObj = {}, appLogType = '' }) {
            new TrackingService().trackAction(
                ACTION_NAMES.TV_PROVIDER_FAIL,
                {
                    clickLabel: '',
                    mvpdPartnerId: providerObj.code,
                    mvpdPartnerName: providerObj.name,
                    mvpdEventFail: 1,
                    userRegType: 'MVPD_AuthSuite',
                    appLogText: appLogType,
                    appLogType: appLogType,
                    appLogCode: 0,
                    authPartnerId: providerObj.code,
                    pageType: 'svod_provider_activationfail',
                    appLog: appLogType,
                },
                true,
            );
        },
    },
};
